/**
 * Member Milestone Progress Dashboard
 * Displays individual member's milestone progress and incentive earnings
 * Integrates with payment infrastructure for payout tracking
 * Trust Marketplace - Configurable Milestone System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Progress } from '../ui/progress';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  DollarSign,
  Target,
  Clock,
  CheckCircle2,
  Calendar,
  Gift,
  Star,
  AlertCircle
} from 'lucide-react';

interface MemberMilestoneProgressProps {
  programId: number;
  memberId: number;
}

interface MemberIncentiveData {
  member_id: number;
  program_id: number;
  enrollment_id: number;
  enrollment_date: string;

  totals: {
    total_cbo_payout_earned: number;
    total_member_payout_earned: number;
    total_points_earned: number;
    pending_member_payout: number;
    paid_member_payout: number;
  };

  milestone_progress: Array<{
    progress_id: number;
    milestone_id: number;
    milestone_name: string;
    current_quantity: number;
    target_quantity: number;
    completion_percentage: number;
    status: string;
    cbo_payout_earned: number;
    member_payout_earned: number;
    points_earned: number;
    first_activity_date?: string;
    completion_date?: string;
    expiration_date?: string;
    incentive_status?: string;
    related_activities?: any;
  }>;
}

const MemberMilestoneProgress: React.FC<MemberMilestoneProgressProps> = ({
  programId,
  memberId
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [data, setData] = useState<MemberIncentiveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMemberProgress();
  }, [programId, memberId]);

  const fetchMemberProgress = async () => {
    try {
      setLoading(true);
      const cboId = localStorage.getItem('active_cbo_id');
      const url = getWeaveinApiUrl(`/api/programs/${programId}/incentives/member/${memberId}`);
      const token = localStorage.getItem('auth_token');

      console.log('Fetching member milestone progress:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || ''
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch member progress: ${response.status}`);
      }

      const result = await response.json();
      console.log('Member progress data:', result);
      setData(result);
    } catch (err) {
      console.error('Error fetching member progress:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  const getStatusColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'EXPIRED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getIncentiveStatusColor = (status?: string): string => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toUpperCase()) {
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'APPROVED':
        return 'bg-blue-100 text-blue-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Error: {error}</span>
        </div>
      </Card>
    );
  }

  if (!data || !data.milestone_progress) {
    return (
      <Card className="p-6 bg-yellow-50 border-yellow-200">
        <div className="flex items-center gap-2 text-yellow-800">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">{t('incentives.no_milestone_data_available', 'No milestone data available')}</span>
        </div>
      </Card>
    );
  }

  const totalEarnable = data.milestone_progress.reduce(
    (sum, m) => sum + m.cbo_payout_earned + m.member_payout_earned,
    0
  );

  const completionRate = data.milestone_progress.length > 0
    ? (data.milestone_progress.filter(m => m.status === 'COMPLETED').length / data.milestone_progress.length) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Member {data.member_id}</h2>
          <p className="text-sm text-gray-600 mt-1">
            Program {data.program_id} • Enrolled {formatDate(data.enrollment_date)}
          </p>
        </div>
      </div>

      {/* Earnings Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Earned */}
        <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">{t('incentives.total_earned', 'Total Earned')}</p>
              <p className="text-3xl font-bold text-green-900 mt-2">
                {formatCurrency(data.totals.paid_member_payout)}
              </p>
            </div>
            <div className="bg-green-500 rounded-full p-3">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Pending Earnings */}
        <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">{t('incentives.pending', 'Pending')}</p>
              <p className="text-3xl font-bold text-yellow-900 mt-2">
                {formatCurrency(data.totals.pending_member_payout)}
              </p>
              <p className="text-xs text-yellow-700 mt-1">{t('incentives.awaiting_approval', 'Awaiting approval')}</p>
            </div>
            <div className="bg-yellow-500 rounded-full p-3">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Total Points */}
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">{t('incentives.total_points', 'Total Points')}</p>
              <p className="text-3xl font-bold text-purple-900 mt-2">
                {data.totals.total_points_earned.toLocaleString()}
              </p>
            </div>
            <div className="bg-purple-500 rounded-full p-3">
              <Star className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Completion Rate */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">{t('incentives.completion_rate', 'Completion Rate')}</p>
              <p className="text-3xl font-bold text-blue-900 mt-2">
                {Math.round(completionRate)}%
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {data.milestone_progress.filter(m => m.status === 'COMPLETED').length} of{' '}
                {data.milestone_progress.length} milestones
              </p>
            </div>
            <div className="bg-blue-500 rounded-full p-3">
              <Target className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Milestone Progress */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('incentives.milestone_progress', 'Milestone Progress')}</h3>
            <p className="text-sm text-gray-600">{t('incentives.track_your_progress_toward_earning', 'Track your progress toward earning incentives')}</p>
          </div>
          <Badge className="bg-purple-100 text-purple-800">
            {data.milestone_progress.filter(m => m.status === 'IN_PROGRESS').length} Active
          </Badge>
        </div>

        {data.milestone_progress.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">{t('incentives.no_milestones_configured_for_this', 'No milestones configured for this program')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.milestone_progress.map((milestone) => {
              const daysRemaining = milestone.expiration_date
                ? Math.ceil(
                    (new Date(milestone.expiration_date).getTime() - new Date().getTime()) /
                      (1000 * 60 * 60 * 24)
                  )
                : null;

              const isExpiringSoon = daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 7;

              return (
                <Card key={milestone.progress_id} className="p-5 bg-gray-50 hover:bg-gray-100 transition-colors">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {milestone.milestone_name}
                        </h4>
                        <Badge className={getStatusColor(milestone.status)}>
                          {milestone.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        {milestone.incentive_status && (
                          <Badge className={getIncentiveStatusColor(milestone.incentive_status)}>
                            Incentive: {milestone.incentive_status}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {milestone.status === 'COMPLETED' && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-600">
                          {formatCurrency(milestone.cbo_payout_earned + milestone.member_payout_earned)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{t('incentives.progress', 'Progress')}</span>
                      <span className="text-sm font-bold text-gray-900">
                        {Math.round(milestone.completion_percentage)}%
                      </span>
                    </div>
                    <div className="relative">
                      <Progress value={milestone.completion_percentage} className="h-3" />
                      <div
                        className={`absolute top-0 left-0 h-3 rounded-full transition-all ${getProgressColor(
                          milestone.completion_percentage
                        )}`}
                        style={{ width: `${milestone.completion_percentage}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                      <span>
                        {milestone.current_quantity} of {milestone.target_quantity}
                      </span>
                      <span>{milestone.target_quantity - milestone.current_quantity} remaining</span>
                    </div>
                  </div>

                  {/* Timeline and Rewards */}
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200">
                    <div>
                      {milestone.first_activity_date && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                          <Calendar className="w-3 h-3" />
                          <span>Started: {formatDate(milestone.first_activity_date)}</span>
                        </div>
                      )}
                      {milestone.completion_date && (
                        <div className="flex items-center gap-2 text-xs text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Completed: {formatDate(milestone.completion_date)}</span>
                        </div>
                      )}
                      {isExpiringSoon && (
                        <div className="flex items-center gap-2 text-xs text-orange-600">
                          <Clock className="w-3 h-3" />
                          <span>{daysRemaining} days remaining</span>
                        </div>
                      )}
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-gray-500 mb-1">{t('incentives.incentive_value', 'Incentive Value')}</div>
                      <div className="text-lg font-bold text-green-600">
                        {formatCurrency(milestone.member_payout_earned)}
                      </div>
                      {milestone.points_earned > 0 && (
                        <div className="text-xs text-purple-600">
                          +{milestone.points_earned} points
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Card>

      {/* Potential Earnings */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="bg-blue-500 rounded-full p-3">
            <Gift className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-gray-900 mb-1">
              {t('incentives.keep_going_more_rewards_available', 'Keep Going! More Rewards Available')}
            </h4>
            <p className="text-sm text-gray-700 mb-3">
              You can earn up to {formatCurrency(totalEarnable)} by completing all program milestones.
              {data.totals.pending_member_payout > 0 && (
                <> You currently have {formatCurrency(data.totals.pending_member_payout)} pending approval.</>
              )}
            </p>
            <div className="flex items-center gap-2">
              <Progress value={completionRate} className="flex-1 h-2" />
              <span className="text-sm font-semibold text-gray-900">
                {Math.round(completionRate)}%
              </span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MemberMilestoneProgress;
